import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { formatImportTimestamp } from '@/hooks/use-import-persistence';
import { onCloudSync } from '@/lib/cloudSyncEvents';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Database, FileBarChart, BarChart3, ShieldCheck, TrendingUp, Loader2, FolderOpen, Search, X, Target, Brain } from 'lucide-react';

interface SavedItem {
  id: string;
  type: 'ibkr' | 'revolut' | 'sma' | 'hedge' | 'sector' | 'outlook';
  label: string;
  date: string;
  route: string;
}

type FilterType = 'all' | 'ibkr' | 'revolut' | 'sma' | 'hedge' | 'sector' | 'outlook';

const typeConfig = {
  ibkr: { icon: FileBarChart, label: 'IBKR', colorClass: 'text-accent' },
  revolut: { icon: TrendingUp, label: 'Revolut', colorClass: 'text-green-500' },
  sma: { icon: BarChart3, label: 'SMA Surveillance', colorClass: 'text-purple-500' },
  hedge: { icon: ShieldCheck, label: 'Hedge Analysis', colorClass: 'text-emerald-500' },
  sector: { icon: Target, label: 'Sector Analysis', colorClass: 'text-amber-500' },
  outlook: { icon: Brain, label: 'Market Outlook', colorClass: 'text-violet-500' },
};

const filterOptions: { value: FilterType; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'ibkr', label: 'IBKR' },
  { value: 'revolut', label: 'Revolut' },
  { value: 'sma', label: 'SMA' },
  { value: 'hedge', label: 'Hedge' },
  { value: 'sector', label: 'Sector' },
  { value: 'outlook', label: 'Outlook' },
];

export function SavedDataMenu() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [items, setItems] = useState<SavedItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [badgeCount, setBadgeCount] = useState(0);
  const [badgePulse, setBadgePulse] = useState(false);
  const prevCountRef = useRef(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const loadSavedData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        setItems([]);
        setLoading(false);
        return;
      }

      const results: SavedItem[] = [];

      const [ibkrRes, revolutRes, smaRes, hedgeRes, sectorRes, outlookRes] = await Promise.all([
        supabase.from('ibkr_imports').select('id, imported_at, account_id, statement_period').order('imported_at', { ascending: false }).limit(5),
        supabase.from('revolut_imports').select('id, imported_at, total_positions, currency').order('imported_at', { ascending: false }).limit(5),
        supabase.from('sma_surveillance_imports').select('id, imported_at, total_stocks, buy_signals, sell_signals').order('imported_at', { ascending: false }).limit(5),
        supabase.from('hedge_analyses').select('id, created_at, positions_count, total_portfolio_value').order('created_at', { ascending: false }).limit(5),
        supabase.from('sector_analyses').select('id, created_at, sector_data, recommendations').order('created_at', { ascending: false }).limit(5),
        supabase.from('market_outlooks').select('id, created_at, models_count').order('created_at', { ascending: false }).limit(5),
      ]);

      if (ibkrRes.data) {
        ibkrRes.data.forEach(d => results.push({
          id: d.id, type: 'ibkr',
          label: `${d.account_id || 'IBKR'} · ${d.statement_period || 'Import'}`,
          date: d.imported_at,
          route: '/ibkr-import',
        }));
      }

      if (revolutRes.data) {
        revolutRes.data.forEach(d => results.push({
          id: d.id, type: 'revolut',
          label: `${d.total_positions ?? 0} positions · ${d.currency || 'USD'}`,
          date: d.imported_at,
          route: '/revolut-import',
        }));
      }

      if (smaRes.data) {
        smaRes.data.forEach(d => results.push({
          id: d.id, type: 'sma',
          label: `${d.total_stocks ?? 0} stocks · ${d.buy_signals ?? 0}B/${d.sell_signals ?? 0}S`,
          date: d.imported_at,
          route: '/sma-surveillance',
        }));
      }

      if (hedgeRes.data) {
        hedgeRes.data.forEach(d => results.push({
          id: d.id, type: 'hedge',
          label: `${d.positions_count} pos · $${Number(d.total_portfolio_value).toLocaleString()}`,
          date: d.created_at,
          route: '/hedge-protection',
        }));
      }

      if (sectorRes.data) {
        sectorRes.data.forEach(d => {
          const sectors = Array.isArray(d.sector_data) ? d.sector_data : [];
          const recs = Array.isArray(d.recommendations) ? d.recommendations : [];
          results.push({
            id: d.id, type: 'sector',
            label: `${sectors.length} sectors · ${recs.length} recommendations`,
            date: d.created_at,
            route: '/hedge-protection',
          });
        });
      }

      if (outlookRes.data) {
        outlookRes.data.forEach(d => results.push({
          id: d.id, type: 'outlook',
          label: `${d.models_count} models · Market Outlook`,
          date: d.created_at,
          route: '/hedge-protection',
        }));
      }

      results.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setItems(results);
    } catch (err) {
      console.error('Failed to load saved data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadSavedData();
      // Focus search after a tick
      setTimeout(() => searchInputRef.current?.focus(), 100);
    } else {
      setSearchQuery('');
      setActiveFilter('all');
    }
  }, [open, loadSavedData]);

  useEffect(() => {
    loadBadgeCount();
  }, []);

  useEffect(() => {
    return onCloudSync((detail) => {
      if (detail.status === 'saved') {
        loadBadgeCount();
      }
    });
  }, []);

  const loadBadgeCount = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) { setItems([]); return; }

      const [ibkr, revolut, sma, hedge, sector, outlook] = await Promise.all([
        supabase.from('ibkr_imports').select('id', { count: 'exact', head: true }),
        supabase.from('revolut_imports').select('id', { count: 'exact', head: true }),
        supabase.from('sma_surveillance_imports').select('id', { count: 'exact', head: true }),
        supabase.from('hedge_analyses').select('id', { count: 'exact', head: true }),
        supabase.from('sector_analyses').select('id', { count: 'exact', head: true }),
        supabase.from('market_outlooks').select('id', { count: 'exact', head: true }),
      ]);

      const total = (ibkr.count ?? 0) + (revolut.count ?? 0) + (sma.count ?? 0) + (hedge.count ?? 0) + (sector.count ?? 0) + (outlook.count ?? 0);
      if (total > prevCountRef.current && prevCountRef.current > 0) {
        setBadgePulse(true);
        setTimeout(() => setBadgePulse(false), 1000);
      }
      prevCountRef.current = total;
      setBadgeCount(total);
    } catch (err) {
      console.error('Failed to load badge count:', err);
    }
  }, []);

  const handleRestore = (item: SavedItem) => {
    setOpen(false);
    const state: any = { autoLoad: true };
    if (item.type === 'sector') {
      state.sectorRestoreId = item.id;
    }
    if (item.type === 'outlook') {
      state.outlookRestoreId = item.id;
    }
    navigate(item.route, { state });
    toast({
      title: "Loading saved data",
      description: `Restoring ${typeConfig[item.type].label} data…`,
    });
  };

  const filteredItems = items.filter((item) => {
    const matchesType = activeFilter === 'all' || item.type === activeFilter;
    if (!matchesType) return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      item.label.toLowerCase().includes(q) ||
      typeConfig[item.type].label.toLowerCase().includes(q) ||
      formatImportTimestamp(item.date).toLowerCase().includes(q)
    );
  });

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2 relative">
          <FolderOpen className="h-4 w-4" />
          <span className="hidden sm:inline">Saved Data</span>
          {badgeCount > 0 && (
            <Badge variant="secondary" className={`h-5 min-w-[20px] px-1 text-[10px] font-bold absolute -top-1 -right-1 transition-transform ${badgePulse ? 'animate-scale-in' : ''}`}>
              {badgeCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-[460px] overflow-hidden flex flex-col">
        <DropdownMenuLabel className="flex items-center gap-2">
          <Database className="h-4 w-4 text-muted-foreground" />
          Saved Data Files
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* Search input */}
        <div className="px-2 pb-1.5" onKeyDown={(e) => e.stopPropagation()}>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              ref={searchInputRef}
              placeholder="Search by name, type, date…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-8 pl-8 pr-8 text-xs"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Type filter chips */}
        <div className="px-2 pb-2 flex gap-1 flex-wrap">
          {filterOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setActiveFilter(opt.value);
              }}
              className={`px-2 py-0.5 rounded-full text-[10px] font-medium border transition-colors ${
                activeFilter === opt.value
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-muted/50 text-muted-foreground border-border hover:bg-muted'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <DropdownMenuSeparator />

        {/* Results */}
        <div className="overflow-y-auto flex-1 max-h-[280px]">
          {loading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">
              {items.length === 0 ? 'No saved data yet' : 'No results match your filter'}
            </div>
          ) : (
            filteredItems.map((item) => {
              const config = typeConfig[item.type];
              const Icon = config.icon;
              return (
                <DropdownMenuItem
                  key={`${item.type}-${item.id}`}
                  className="flex items-start gap-3 py-2.5 px-3 cursor-pointer"
                  onClick={() => handleRestore(item)}
                >
                  <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${config.colorClass}`} />
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0">
                        {config.label}
                      </Badge>
                      <span className="text-xs text-muted-foreground truncate">
                        {formatImportTimestamp(item.date)}
                      </span>
                    </div>
                    <span className="text-xs text-foreground truncate">{item.label}</span>
                  </div>
                </DropdownMenuItem>
              );
            })
          )}
        </div>

        {items.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="justify-center text-sm text-muted-foreground cursor-pointer"
              onClick={() => { setOpen(false); navigate('/collected-data'); }}
            >
              View all collected data →
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
